import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import Sutra from "./pages/Sutra.tsx";
const Landing = lazy(() => import("./pages/Landing.tsx"));
import RequireAdmin from "./components/RequireAdmin.tsx";
import RequireAuth from "./components/RequireAuth.tsx";
import StaleModuleErrorBoundary from "./components/StaleModuleErrorBoundary.tsx";

// Lazy-load non-landing routes to shrink the initial bundle.
const Index = lazy(() => import("./pages/Index.tsx"));
const GlossaryStatusPanel = lazy(
  () => import("./components/scripture/GlossaryStatusPanel.tsx")
);
const TraditionDetail = lazy(() => import("./pages/TraditionDetail.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const VersePage = lazy(() => import("./pages/VersePage.tsx"));
const VersePreview = lazy(() => import("./pages/VersePreview.tsx"));
const Daily = lazy(() => import("./pages/Daily.tsx"));
const DailyPreview = lazy(() => import("./pages/DailyPreview.tsx"));
const ManageSubscription = lazy(() => import("./pages/ManageSubscription.tsx"));
const PrintNotesPage = lazy(() => import("./pages/PrintNotesPage.tsx"));
const AdminUsers = lazy(() => import("./pages/AdminUsers.tsx"));
const AdminOgPreview = lazy(() => import("./pages/AdminOgPreview.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminContentHealth = lazy(() => import("./pages/admin/AdminContentHealth.tsx"));
const AdminTraditions = lazy(() => import("./pages/admin/AdminTraditions.tsx"));
const AdminTexts = lazy(() => import("./pages/admin/AdminTexts.tsx"));
const AdminCovers = lazy(() => import("./pages/admin/AdminCovers.tsx"));
const AdminChapters = lazy(() => import("./pages/admin/AdminChapters.tsx"));
const AdminVerses = lazy(() => import("./pages/admin/AdminVerses.tsx"));
const AdminImport = lazy(() => import("./pages/admin/AdminImport.tsx"));
const AdminRamcharitmanasImport = lazy(() => import("./pages/admin/AdminRamcharitmanasImport.tsx"));
const AdminDictionaryImport = lazy(() => import("./pages/admin/AdminDictionaryImport.tsx"));
const AdminDictionary = lazy(() => import("./pages/admin/AdminDictionary.tsx"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers.tsx"));
const AdminLensDrafts = lazy(() => import("./pages/admin/AdminLensDrafts.tsx"));
const AdminHiddenBooks = lazy(() => import("./pages/admin/AdminHiddenBooks.tsx"));
const AdminRamcharitmanasGlossary = lazy(() => import("./pages/admin/AdminRamcharitmanasGlossary.tsx"));
const AdminGoLive = lazy(() => import("./pages/admin/AdminGoLive.tsx"));
const AdminPerformance = lazy(() => import("./pages/admin/AdminPerformance.tsx"));
const AdminPageAnalytics = lazy(() => import("./pages/admin/AdminPageAnalytics.tsx"));
const SacredTextsPage = lazy(() => import("./pages/SacredTextsPage.tsx"));
const AboutSources = lazy(() => import("./pages/AboutSources.tsx"));
const AuthPage = lazy(() => import("./pages/AuthPage.tsx"));
const RamcharitmanasReader = lazy(() => import("./pages/RamcharitmanasReader.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const SandhiGlossaryPage = lazy(() => import("./pages/SandhiGlossaryPage.tsx"));
const PageViewTracker = lazy(() => import("./components/PageViewTracker.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={null}>
            <PageViewTracker />
          </Suspense>
          <StaleModuleErrorBoundary>
            <Suspense fallback={null}>
              <Routes>
              <Route path="/" element={<Sutra />} />
              <Route path="/dharmalens" element={<Landing />} />
              <Route path="/traditions/:slug" element={<TraditionDetail />} />
              <Route path="/app" element={<RequireAuth><Index /></RequireAuth>} />
              <Route path="/app/sacred-texts" element={<RequireAuth><SacredTextsPage /></RequireAuth>} />
              <Route path="/about/sources" element={<AboutSources />} />
              <Route path="/glossary/sandhi" element={<SandhiGlossaryPage />} />
              <Route path="/glossary/sandhi/:category" element={<SandhiGlossaryPage />} />
              <Route path="/ramcharitmanas" element={<RamcharitmanasReader />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/verse/:book/:chapter/:verse" element={<VersePage />} />
              <Route
                path="/verse/:id/preview"
                element={
                  <RequireAdmin>
                    <VersePreview />
                  </RequireAdmin>
                }
              />
              <Route path="/daily" element={<Daily />} />
              <Route
                path="/daily/preview"
                element={
                  <RequireAdmin>
                    <DailyPreview />
                  </RequireAdmin>
                }
              />
              <Route path="/daily/manage" element={<ManageSubscription />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/print/notes" element={<PrintNotesPage />} />
              <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
              <Route path="/admin/content-health" element={<RequireAdmin><AdminContentHealth /></RequireAdmin>} />
              <Route path="/admin/traditions" element={<RequireAdmin><AdminTraditions /></RequireAdmin>} />
              <Route path="/admin/texts" element={<RequireAdmin><AdminTexts /></RequireAdmin>} />
              <Route path="/admin/covers" element={<RequireAdmin><AdminCovers /></RequireAdmin>} />
              <Route path="/admin/chapters" element={<RequireAdmin><AdminChapters /></RequireAdmin>} />
              <Route path="/admin/verses" element={<RequireAdmin><AdminVerses /></RequireAdmin>} />
              <Route path="/admin/import" element={<RequireAdmin><AdminImport /></RequireAdmin>} />
              <Route path="/admin/import-ramcharitmanas" element={<RequireAdmin><AdminRamcharitmanasImport /></RequireAdmin>} />
              <Route path="/admin/dictionary" element={<RequireAdmin><AdminDictionary /></RequireAdmin>} />
              <Route path="/admin/import-dictionary" element={<RequireAdmin><AdminDictionaryImport /></RequireAdmin>} />
              <Route path="/admin/subscribers" element={<RequireAdmin><AdminSubscribers /></RequireAdmin>} />
              <Route path="/admin/lens-drafts" element={<RequireAdmin><AdminLensDrafts /></RequireAdmin>} />
              <Route path="/admin/hidden-books" element={<RequireAdmin><AdminHiddenBooks /></RequireAdmin>} />
              <Route path="/admin/ramcharitmanas-glossary" element={<RequireAdmin><AdminRamcharitmanasGlossary /></RequireAdmin>} />
              <Route path="/admin/go-live" element={<RequireAdmin><AdminGoLive /></RequireAdmin>} />
              <Route path="/admin/performance" element={<RequireAdmin><AdminPerformance /></RequireAdmin>} />
              <Route path="/admin/page-analytics" element={<RequireAdmin><AdminPageAnalytics /></RequireAdmin>} />
              <Route
                path="/admin/users"
                element={
                  <RequireAdmin>
                    <AdminUsers />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/og-preview"
                element={
                  <RequireAdmin>
                    <AdminOgPreview />
                  </RequireAdmin>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              <Suspense fallback={null}>
                <GlossaryStatusPanel />
              </Suspense>
            </Suspense>
          </StaleModuleErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
